import { ReportSettings } from './report-settings.interface';
import { ReportSettingsDto, UserSettingsForm } from './account';
import { ThemeMode } from '@/core/services/theme.service';
import { CloneUtils } from '@/core/utils/clone-utils';
import { SelectOption } from '@/core/types/angular-primeng-api';
import { ReportSettingDefaults } from '@/core/services/report-settings/report-setting-defaults';
import { isTimeFormat, TimeFormat } from './time-format';
import { DataUtils } from '@/core/utils/data-utils';

export type UserSettingsUpdate = Partial<Omit<UserSettings, 'reportSettings'>> & {
    reportSettings?: Partial<ReportSettings>;
};

export class UserSettings {
    color: ThemeMode;
    language: string;
    timeFormat: TimeFormat;
    dateFormat: string;
    apiKey: string = '';
    // TODO make reportSettings private, so that nobody tries to access it directly!
    reportSettings: ReportSettings;

    constructor() {
        this.color = 'light';
        this.language = 'en';
        this.timeFormat = 'HH:mm';
        this.dateFormat = 'YYYY.MM.DD';
        this.reportSettings = ReportSettingDefaults.getUserInitSettings();
    }

    public static fromJson(json: UserSettingsForm | undefined): UserSettings {
        const s = new UserSettings();
        if (json !== undefined && json !== null) {
            if (json.color === 'light' || json.color === 'dark' || json.color === 'system') {
                s.color = json.color;
            } else {
                s.color = 'light';
            }
            s.language = json.language;
            s.timeFormat = isTimeFormat(json.timeFormat) ? json.timeFormat : 'HH:mm';
            s.dateFormat = json.dateFormat;
            s.apiKey = json.apiKey;
            s.reportSettings = this.getReportSettings(json.reportSettings);
        }
        return s;
    }

    // TODO remove the SelectOptions from ReportSettings type and by this remove this mapping!
    private static getReportSettings(reportSettingsDto: ReportSettingsDto): ReportSettings {
        const cpy = CloneUtils.cloneData(reportSettingsDto) as ReportSettings;
        this.fixSelectOption(cpy, 'dateFormat');
        this.fixSelectOption(cpy, 'timeFormat');
        this.fixSelectOption(cpy, 'durationFormat');
        return cpy;
    }

    /**
     * This converts broken user settings stored in the db (SelectionOption elements instead of only the code) to the code (string)
     */
    private static fixSelectOption(settings: ReportSettings, fieldName: keyof ReportSettings): void {
        if (settings[fieldName] && typeof (settings[fieldName] as unknown) !== 'string') {
            const selectOption = settings[fieldName] as unknown as SelectOption;
            if (selectOption.code) {
                (settings[fieldName] as string) = selectOption.code;
            } else {
                const msg = `UserSettings.fixSelectOption(): Unexpected data received for field "${fieldName}"`;
                console.error(msg, selectOption);
            }
        }
    }

    /**
     * Applies the defined values of `changes` to a copy of the given settings.
     *
     * This is a first-level merge: supplied top-level fields replace their complete values and nested fields are
     * not merged. `reportSettings` is the exception. Its supplied fields are merged one level deeper, so
     * `{ reportSettings: { dateFormat: 'YYYY-MM-DD' } }` changes only `dateFormat` and retains the other report
     * settings. This matters because the backend saves `reportSettings` as a whole.
     *
     * @param settings - The settings to merge the changes into. The object itself is not modified.
     * @param changes - The values to apply. Undefined values are ignored.
     * @returns A new UserSettings with the changes applied.
     * Use {@link UserSettings.toJson} on it to get the request form.
     * @throws Error if `settings` or its `reportSettings` are missing. Both are always initialized by the
     * constructor, so this only guards against a broken object and never happens in normal use.
     */
    public static mergeUpdate(settings: UserSettings, changes: UserSettingsUpdate): UserSettings {
        if (!settings) {
            throw new Error('UserSettings.mergeUpdate(): settings is not defined, there is nothing to merge into.');
        }
        const merged = CloneUtils.cloneClassSafe(settings);
        const { reportSettings, ...otherSettings } = changes;
        DataUtils.patchObject(merged, otherSettings);
        if (reportSettings) {
            if (!merged.reportSettings) {
                // Not expected: the constructor always initializes reportSettings. Fail with a clear message
                // instead of an obscure TypeError inside patchObject(). Merging into a missing value would
                // mean sending partial report settings, and the backend replaces them as a whole.
                throw new Error(
                    'UserSettings.mergeUpdate(): settings.reportSettings is not defined, cannot merge the supplied report settings.',
                );
            }
            DataUtils.patchObject(merged.reportSettings, reportSettings);
        }
        return merged;
    }

    public static toJson(settings: UserSettings): UserSettingsForm {
        return {
            color: settings.color,
            language: settings.language,
            dateFormat: settings.dateFormat,
            timeFormat: settings.timeFormat,
            apiKey: settings.apiKey,
            // The following behavior mimics the current / legacy logic - the unknown fields are not transmitted
            // startWeekWithSunday: false, // does not exist on UserSettings
            // timeRasterization: null, // does not exist on UserSettings
            // tileColorFromLayer: null, // does not exist on UserSettings
            reportSettings: this.getReportSettingsDto(settings.reportSettings),
        } as unknown as UserSettingsForm;
    }

    private static getReportSettingsDto(reportSettings: ReportSettings): ReportSettingsDto {
        return reportSettings as unknown as ReportSettingsDto; // TODO check if this is safe without cloning - should be fine
        // return CloneUtils.cloneData(reportSettings) as ReportSettingsDto;
    }
}
